import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPurchaseByTransType } from "../../../services/PurchaseApiService";
import { fetchProduct, fetchSupplier } from "../../../redux/userSetting";
import { fetchBranchSettings } from "../../../redux/settingbyBranch";
import { getWareHouseList } from "../../../services/MasterApiServices";
import { ReportScreen } from "../../../components/report";
import {
  dateOf,
  dmy,
  groupSum,
  intg,
  money,
  num,
  sumBy,
} from "../../../components/report/reportFormat";

// the payment map this report already used for its Status column
const PAYMENTS = [
  { v: 0, t: "Cash", tone: "cash" },
  { v: 1, t: "Card", tone: "card" },
  { v: 2, t: "Bank", tone: "upi" },
  { v: 3, t: "Credit", tone: "credit" },
  { v: 4, t: "UPI", tone: "upi" },
];

const GoodsReceiptReport = () => {
  const dispatch = useDispatch();
  const { branch } = useSelector((state) => state.branchlist);
  const { formatedproductList, formatedsupplierList } = useSelector(
    (state) => state.usersetting
  );

  const [purchaseData, setPurchaseData] = useState([]);
  const [wareHouseList, setWareHouseList] = useState([]);

  // goods receipts are purchases with trans type 1
  const handlePurchaseData = async () => {
    try {
      const response = await getPurchaseByTransType(branch, 1);
      setPurchaseData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(
        "Failed to fetch goods receipt",
        error?.response?.data?.Message || "something went wrong"
      );
    }
  };

  useEffect(() => {
    dispatch(fetchSupplier());
  }, []);

  useEffect(() => {
    dispatch(fetchProduct(branch));
    dispatch(fetchBranchSettings(branch.guid));
    handlePurchaseData();
  }, [branch]);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await getWareHouseList();
        setWareHouseList(Array.isArray(response) ? response : []);
      } catch (err) {
        console.error("Failed to fetch warehouses:", err);
      }
    };
    fetchWarehouses();
  }, []);

  const warehouseMap = useMemo(
    () =>
      Object.fromEntries(
        wareHouseList.map((w) => [w.guid, w.name || "Unnamed Warehouse"])
      ),
    [wareHouseList]
  );

  /* one flat record per goods receipt */
  const rows = useMemo(
    () =>
      purchaseData.map((inv) => {
        const pay = PAYMENTS.find((p) => p.v === inv.purchasePaymentType);
        return {
          guid: inv.guid,
          date: dateOf(inv.date),
          billNo: inv.entrynumber || "",
          supplier: inv.supplier?.name || "Unknown Supplier",
          supplierGuid: inv.supplier?.guid,
          grnNo: inv.invoiceNo || "",
          grnDate: dateOf(inv.invoiceDate),
          deliveryDate: dateOf(inv.duedate),
          warehouse:
            warehouseMap[inv.wareHouse?.guid] || inv.wareHouse?.name || "-",
          warehouseGuid: inv.wareHouse?.guid,
          payment: pay?.t || "",
          paymentType: inv.purchasePaymentType,
          total: num(inv.grossTotal),
          discount: num(inv.discountTotal),
          tax: num(inv.taxTotal),
          net: num(inv.netTotal),
          lines: inv.purchaseDetails || [],
        };
      }),
    [purchaseData, warehouseMap]
  );

  /* ---------------- filters ---------------- */
  const filters = useMemo(
    () => [
      {
        key: "products",
        label: "Products",
        items: (formatedproductList || []).map((p) => ({
          v: p.value,
          t: p.label || "—",
        })),
        match: (r, v) => r.lines.some((l) => v.includes(l?.product?.guid)),
      },
      {
        key: "suppliers",
        label: "Suppliers",
        items: (formatedsupplierList || []).map((s) => ({
          v: s.value,
          t: s.label || "—",
        })),
        match: (r, v) => v.includes(r.supplierGuid),
      },
      {
        key: "warehouses",
        label: "Warehouses",
        items: wareHouseList.map((w) => ({
          v: w.guid,
          t: w.name || "Unnamed Warehouse",
        })),
        match: (r, v) => v.includes(r.warehouseGuid),
      },
    ],
    [formatedproductList, formatedsupplierList, wareHouseList]
  );

  const searchMatch = (r, q) => {
    const hit = (s) => String(s || "").toLowerCase().includes(q);
    return (
      hit(r.billNo) ||
      hit(r.supplier) ||
      hit(r.grnNo) ||
      hit(r.grnDate) ||
      hit(r.warehouse) ||
      hit(r.date) ||
      hit(r.payment) ||
      String(r.net).includes(q) ||
      r.lines.some((l) => hit(l?.product?.name) || hit(l?.product?.code))
    );
  };

  /* ---------------- tabs ---------------- */
  const tabs = useMemo(
    () => [
      {
        id: "detail",
        label: "Detailed Reports",
        defaultSort: { k: "date", d: -1 },
        rowKey: (r) => r.guid,
        groupOptions: [
          { v: "supplier", t: "Supplier" },
          { v: "warehouse", t: "Warehouse" },
          { v: "payment", t: "Payment" },
          { v: "__month", t: "Month" },
        ],
        columns: [
          { k: "sl", t: "Sl No", type: "index" },
          { k: "date", t: "Date", type: "date" },
          { k: "billNo", t: "Bill No", type: "code" },
          { k: "supplier", t: "Supplier", type: "avatar" },
          { k: "warehouse", t: "Warehouse" },
          { k: "grnNo", t: "GRN No.", type: "code", off: true },
          { k: "grnDate", t: "GRN Date", type: "date", off: true },
          { k: "deliveryDate", t: "Delivery Date", type: "date", off: true },
          {
            k: "payment",
            t: "Status",
            type: "pill",
            pill: (r) => ({
              text: r.payment,
              tone: PAYMENTS.find((p) => p.v === r.paymentType)?.tone,
            }),
          },
          { k: "total", t: "Total", type: "money" },
          { k: "discount", t: "Discount", type: "money" },
          { k: "tax", t: "Tax", type: "money" },
          { k: "net", t: "Net Amount", type: "money" },
        ],
      },
      {
        id: "supplier",
        label: "Supplier Reports",
        defaultSort: { k: "net", d: -1 },
        rowKey: (r) => r.key,
        drillDown: false,
        rows: (list) => {
          const g = groupSum(
            list,
            (r) => r.supplierGuid || r.supplier,
            ["total", "discount", "tax", "net"],
            (r) => ({ supplier: r.supplier, last: "" })
          );
          g.forEach((row) => {
            row.last = list
              .filter((r) => (r.supplierGuid || r.supplier) === row.key)
              .reduce((a, r) => (r.date > a ? r.date : a), "");
            row.avg = row.n ? row.net / row.n : 0;
          });
          return g;
        },
        columns: [
          { k: "supplier", t: "Supplier", type: "avatar" },
          { k: "n", t: "Receipts", type: "int" },
          { k: "total", t: "Total", type: "money" },
          { k: "discount", t: "Discount", type: "money" },
          { k: "tax", t: "Tax", type: "money" },
          { k: "net", t: "Net Amount", type: "money" },
          { k: "avg", t: "Avg Receipt", type: "money", noSum: true },
          { k: "last", t: "Last Receipt Date", type: "date" },
          { k: "share", t: "Share of Net", type: "share", shareOf: "net" },
        ],
      },
      {
        id: "item",
        label: "Itemwise Reports",
        defaultSort: { k: "net", d: -1 },
        rowKey: (r) => r.key,
        drillDown: false,
        rows: (list) => {
          const m = {};
          list.forEach((r) =>
            r.lines.forEach((l) => {
              const k = l?.product?.guid || l?.product?.name || "unknown";
              if (!m[k])
                m[k] = {
                  key: k,
                  code: l?.product?.code || "—",
                  itemName: l?.product?.name || "Unknown Item",
                  unit: l?.unit?.name || "",
                  n: 0,
                  quantity: 0,
                  total: 0,
                  discount: 0,
                  tax: 0,
                  net: 0,
                };
              const g = m[k];
              const qty = num(l.quantity);
              g.n += 1;
              g.quantity += qty;
              g.total += num(l.grossAmount ?? qty * num(l.price));
              g.discount += num(l.discountAmount);
              g.tax += num(l.taxAmount);
              g.net += num(l.netAmount);
            })
          );
          return Object.values(m).map((g) => ({
            ...g,
            rate: g.quantity ? g.total / g.quantity : 0,
          }));
        },
        columns: [
          { k: "code", t: "Item Code", type: "code" },
          { k: "itemName", t: "Item Name" },
          { k: "unit", t: "Unit", off: true },
          { k: "n", t: "Lines", type: "int" },
          { k: "quantity", t: "Quantity", type: "int" },
          { k: "rate", t: "Rate", type: "money", noSum: true },
          { k: "total", t: "Total", type: "money" },
          { k: "discount", t: "Discount", type: "money" },
          { k: "tax", t: "Tax", type: "money" },
          { k: "net", t: "Net Amount", type: "money" },
          { k: "share", t: "Share of Net", type: "share", shareOf: "net" },
        ],
      },
      {
        id: "daily",
        label: "Day-wise",
        defaultSort: { k: "date", d: -1 },
        rowKey: (r) => r.key,
        drillDown: false,
        rows: (list) => {
          const g = groupSum(list, (r) => r.date, [
            "total",
            "discount",
            "tax",
            "net",
          ]);
          return g.map((row) => ({
            ...row,
            date: row.key,
            avg: row.n ? row.net / row.n : 0,
          }));
        },
        columns: [
          { k: "date", t: "Date", type: "date" },
          { k: "n", t: "Receipts", type: "int" },
          { k: "total", t: "Total", type: "money" },
          { k: "discount", t: "Discount", type: "money" },
          { k: "tax", t: "Tax", type: "money" },
          { k: "net", t: "Net Amount", type: "money" },
          { k: "avg", t: "Avg Receipt", type: "money", noSum: true },
          { k: "share", t: "Share of Net", type: "share", shareOf: "net" },
        ],
      },
    ],
    []
  );

  /* ---------------- KPIs ---------------- */
  const kpis = useMemo(
    () => [
      { label: "Goods receipts", value: (list) => list.length, format: "int" },
      { label: "Gross total", value: (list) => sumBy(list, "total") },
      {
        label: "Discount",
        value: (list) => sumBy(list, "discount"),
        invert: true,
      },
      { label: "Tax", value: (list) => sumBy(list, "tax") },
      { label: "Net amount", value: (list) => sumBy(list, "net"), hero: true },
      {
        label: "Average receipt",
        value: (list) => (list.length ? sumBy(list, "net") / list.length : 0),
      },
    ],
    []
  );

  /* ---------------- drill-down ---------------- */
  const drawerContent = (r) => ({
    title: r.supplier,
    subtitle: `${r.billNo} · ${dmy(r.date)}`,
    avatarKey: r.supplier,
    meta: [
      { k: "Warehouse", v: r.warehouse },
      { k: "GRN No.", v: r.grnNo || "—" },
      { k: "GRN Date", v: r.grnDate ? dmy(r.grnDate) : "—" },
      { k: "Delivery Date", v: r.deliveryDate ? dmy(r.deliveryDate) : "—" },
      { k: "Status", v: r.payment },
      { k: "Lines", v: intg((r.lines || []).length) },
    ],
    lines: {
      title: "Receipt lines",
      columns: [
        { t: "Item" },
        { t: "Qty", align: "right" },
        { t: "Rate", align: "right" },
        { t: "Disc", align: "right" },
        { t: "Amount", align: "right" },
      ],
      rows: (r.lines || []).map((l) => [
        <>
          <div className="axr-nm">{l?.product?.name || "Unknown Item"}</div>
          <div className="axr-cd">
            {[l?.product?.code, l?.unit?.name].filter(Boolean).join(" · ")}
          </div>
        </>,
        intg(l?.quantity),
        money(l?.price),
        num(l?.discountAmount) ? money(l.discountAmount) : "—",
        money(l?.netAmount),
      ]),
    },
    totals: [
      { l: "Gross", v: money(r.total) },
      { l: "Discount", v: r.discount ? "−" + money(r.discount) : "0.00" },
      { l: "Tax", v: money(r.tax) },
    ],
    net: { l: "Net amount", v: money(r.net) },
  });

  return (
    <ReportScreen
      title="Goods Receipt Report"
      subtitle="Every goods receipt booked in the selected period"
      unit="receipts"
      rows={rows}
      tabs={tabs}
      filters={filters}
      kpis={kpis}
      chart={{ valueKey: "net", title: "Net goods receipts over time" }}
      searchMatch={searchMatch}
      searchPlaceholder="Bill no, supplier, item, GRN no…"
      exportName="GoodsReceipt"
      reportNames={{
        detail: "Goods Receipt Detailed Reports",
        supplier: "Goods Receipt Supplier Reports",
        item: "Goods Receipt Itemwise Reports",
        daily: "Goods Receipt Day Wise",
      }}
      onRefresh={handlePurchaseData}
      drawerContent={drawerContent}
    />
  );
};

export default GoodsReceiptReport;
